/**
 * Parent groups used by the parameter filter UI.
 *
 * Selecting a parent selects the whole group. Internally, the parent filter
 * key represents all of its children.
 *
 * Selecting a child removes its parent from the active filters and toggles
 * only that child. Selecting the parent again removes any active children and
 * restores the whole group selection.
 */
export const parameterFilterGroups = {
    'group.displacements': {
        children: ['thrust', 'sway', 'lift'],
        label: 'parameter_selection.groups.displacements',
    },
    'group.angles': {
        children: ['rotation', 'tilt', 'bend', 'alignment'],
        label: 'parameter_selection.groups.angles',
    },
    'group.other': {
        children: ['other', 'metric.angle', 'metric.duration', 'ball'],
        label: 'parameter_selection.groups.other',
    },
} as const;

export const parameterFilters = [
    ...parameterFilterGroups['group.displacements'].children,
    ...parameterFilterGroups['group.angles'].children,
    ...parameterFilterGroups['group.other'].children,
] as const;

export type FilterKey = (typeof parameterFilterGroups)[keyof typeof parameterFilterGroups]['children'][number];
export type FilterGroupKey = keyof typeof parameterFilterGroups;
export type FilterSelectionKey = FilterKey | FilterGroupKey;

type ParameterFilterGroupEntry = [FilterGroupKey, (typeof parameterFilterGroups)[FilterGroupKey]];

/**
 * Object.entries widens known object keys to string. Restore the filter-group
 * key type here so consumers can iterate over typed group entries.
 */
export const parameterFilterGroupEntries = Object.entries(
    parameterFilterGroups,
) as readonly ParameterFilterGroupEntry[];

/**
 * Labels for parameter filters.
 *
 * The filter key also describes which parameter field is matched:
 *
 * Filter keys are matched against the parameter's filter tags.
 */
export const parameterFilterLabels: Record<FilterKey, string> = {
    thrust: 'parameter_selection.filters.thrust',
    sway: 'parameter_selection.filters.sway',
    lift: 'parameter_selection.filters.lift',
    rotation: 'parameter_selection.filters.rotation',
    tilt: 'parameter_selection.filters.tilt',
    bend: 'parameter_selection.filters.bend',
    alignment: 'parameter_selection.filters.joint_angle',
    ['metric.angle']: 'parameter_selection.filters.angle',
    ['metric.duration']: 'parameter_selection.filters.duration',
    other: 'parameter_selection.filters.other',
    ball: 'parameter_selection.filters.ball',
} as const;
